import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  IconButton,
  Stack,
  useTheme,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Assets
import meeshoImg from '../../assets/Meesho_logo.png';
import flipkartImg from '../../assets/flipkart_logo.png';
import amazonImg from '../../assets/amazon.webp';
import myntraImg from '../../assets/myntra.jpg';
import InstagramImg from '../../assets/insta.jpeg';

const shops = [
  { name: 'Meesho', image: meeshoImg, color: '#f43397', delay: 0.1 },
  { name: 'Flipkart', image: flipkartImg, color: '#047bd5', delay: 0.2 },
  { name: 'Amazon', image: amazonImg, color: '#ff9900', delay: 0.3 },
  { name: 'Instagram', image: InstagramImg, color: '#e4405f', delay: 0.4 },
  { name: 'Myntra', image: myntraImg, color: '#ff3f6c', delay: 0.5 },
];

const ShopGridSimple = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [hoveredCard, setHoveredCard] = React.useState(null);

  const handleCardClick = (shopName) => {
    navigate('/menu', { state: { shopName } });
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardItem = (delay) => ({
    hidden: { y: 40, opacity: 0, scale: 0.95 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        delay,
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  });

  const cardHover = {
    y: -15,
    scale: 1.05,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  };

  const imageHover = {
    scale: 1.1,
    transition: {
      duration: 0.4,
    },
  };

  const rippleEffect = {
    scale: 1.2,
    opacity: 0,
    transition: {
      duration: 0.6,
    },
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Title & Back Button */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: { xs: 2, md: 10 } }}
>
          <motion.div whileTap={{ scale: 0.9 }}>
            <IconButton
              onClick={handleBackClick}
              sx={{
                backgroundColor: theme.palette.grey[100],
                '&:hover': {
                  backgroundColor: theme.palette.grey[200],
                },
              }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          </motion.div>
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.5px',
            }}
          >
            Popular Stores
          </Typography>
        </Stack>
      </motion.div>

      {/* Grid */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={container}
        style={{ width: '100%' }}
      >
<Grid container spacing={{ xs: 2, md: 7 }} justifyContent="center">

          {shops.map((shop, index) => (
            <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
              <motion.div
                variants={cardItem(shop.delay)}
                whileHover={cardHover}
                onHoverStart={() => setHoveredCard(index)}
                onHoverEnd={() => setHoveredCard(null)}
                style={{ position: 'relative', height: '100%' }}
              >
         <Card
  onClick={() => handleCardClick(shop.name)}
  sx={{
    height: { xs: 80, sm: 220, md: 260 },
    width: '100%',
    display: 'flex',
    flexDirection: { xs: 'row', sm: 'column' }, // ✅ row on mobile
    alignItems: 'center',
    justifyContent: { xs: 'flex-start', sm: 'space-between' },
    gap: { xs: 5, sm: 5 },
    p: { xs: 2, sm: 3 },
    borderRadius: 4,
    boxShadow: `0 12px 24px ${shop.color}50`,
    cursor: 'pointer',
    background: `linear-gradient(145deg, ${shop.color}08, #ffffff)`,
    border: `1px solid ${shop.color}15`,
    overflow: 'hidden',
    position: 'relative',
  }}
>

                  <AnimatePresence>
                    {hoveredCard === index && (
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={rippleEffect}
                        exit={{ opacity: 0 }}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: `radial-gradient(circle at center, ${shop.color}15 0%, transparent 70%)`,
                          zIndex: 0,
                        }}
                      />
                    )}
                  </AnimatePresence>

                  {/* Glow effect */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: `radial-gradient(circle at 50% 0%, ${shop.color}10, transparent 70%)`,
                      opacity: hoveredCard === index ? 1 : 0,
                      transition: 'opacity 0.3s ease',
                    }}
                  />

                  {/* Logo */}
                  <motion.div
                    whileHover={imageHover}
                    style={{ position: 'relative', zIndex: 1 }}
                  >
        <Box
  component="img"
  src={shop.image}
  alt={shop.name}
  sx={{
    height: { xs: 60, sm: 100 },
    width: { xs: 60, sm: '100%' },
    objectFit: 'contain',
    mb: { xs: 0, sm: 2 },
    filter: 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.15))',
  }}
/>

                  </motion.div>
<CardContent
  sx={{
    textAlign: { xs: 'left', sm: 'center' },
    ml: { xs: 2, sm: 0 },
    p: 0,
    position: 'relative',
    zIndex: 1,
  }}
>

                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{
                        color: theme.palette.text.primary,
                        mb: 0.5,
                        textShadow: `0 2px 4px ${shop.color}20`,
                      }}
                    >
                      {shop.name}
                    </Typography>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: hoveredCard === index ? '60%' : '40%',
                        transition: { duration: 0.3 },
                      }}
                      style={{
                        height: '2px',
                        background: `linear-gradient(90deg, ${shop.color}, ${shop.color}80)`,
                        margin: '0 auto',
                        borderRadius: '2px',
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        display: 'block',
                        mt: 1.5,
                        opacity: hoveredCard === index ? 1 : 0.8,
                        transition: 'opacity 0.2s ease',
                      }}
                    >
                      Explore deals
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </motion.div>

      {/* Footer Text */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        style={{ marginTop: '4rem' }}
      >
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            textAlign: 'center',
            maxWidth: '500px',
            margin: '0 auto',
            position: 'relative',
            '&:before, &:after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              width: '30%',
              height: '1px',
              background: `linear-gradient(90deg, transparent, ${theme.palette.divider})`,
            },
            '&:before': {
              left: 0,
            },
            '&:after': {
              right: 0,
              background: `linear-gradient(90deg, ${theme.palette.divider}, transparent)`,
            },
          }}
        >
          Select your favorite shopping destination
        </Typography>
      </motion.div>
    </Box>
  );
};

export default ShopGridSimple;
